import { useNavigate } from "react-router-dom";
import { startLifeguideHandoff } from "@/lib/api";
import { toast } from "sonner";
import { Anchor, ArrowRight, Clock, MapPin, HeartHandshake, Users, Sparkles } from "lucide-react";

// Fictitious church-partner demo page. Self-contained; does not affect the main app.
const ACCENT = "#35657B"; // calm harbor blue
const ACCENT_DARK = "#264B5C";

export default function Harborstone() {
  const nav = useNavigate();

  const lifeguide = async () => {
    try {
      const r = await startLifeguideHandoff({ source_partner: "harborstone_demo" });
      if (!r.ok && r.reason === "not_configured") toast.error("Could not open LifeGuide right now. Please try again.");
      else if (!r.ok) toast.error("Could not start the LifeGuide handoff. Please try again.");
    } catch (e) {
      if (e.response?.status === 401) { toast.error("Please log in to continue with LifeGuide."); nav("/login"); }
      else toast.error("LifeGuide handoff failed. Please try again in a moment.");
    }
  };

  return (
    <div className="bg-[#FDFBF7]">
      {/* Demo banner */}
      <div data-testid="harborstone-demo-banner" className="w-full text-center text-xs sm:text-sm py-2 px-4 font-medium text-white" style={{ backgroundColor: ACCENT_DARK }}>
        <Sparkles className="inline w-3.5 h-3.5 mr-1.5 -mt-0.5" />
        Demo church partner page — Harborstone Community Church is a fictional example.
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10" style={{ background: `radial-gradient(120% 80% at 50% 0%, ${ACCENT}14 0%, #FDFBF7 60%)` }} />
        <div className="max-w-4xl mx-auto px-5 sm:px-8 pt-14 sm:pt-20 pb-14 text-center">
          {/* Logo */}
          <div data-testid="harborstone-logo" className="inline-flex items-center gap-3 mb-8">
            <span className="grid place-items-center w-12 h-12 rounded-2xl shadow-sm" style={{ backgroundColor: ACCENT }}>
              <Anchor className="w-6 h-6 text-white" strokeWidth={1.8} />
            </span>
            <span className="text-left leading-tight">
              <span className="block font-serif-display text-2xl sm:text-3xl font-semibold tracking-tight text-[#0F172A]">Harborstone</span>
              <span className="block text-[11px] sm:text-xs uppercase tracking-[0.25em]" style={{ color: ACCENT }}>Community Church</span>
            </span>
          </div>

          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] tracking-tight text-[#0F172A]">
            Welcome to I Need a Proverb — a resource from Harborstone Community Church
          </h1>
          <p className="mt-6 text-lg sm:text-xl text-[#475569] max-w-2xl mx-auto leading-relaxed">
            When life feels heavy, God's wisdom can meet you right where you are. Find a Proverb for what you are facing today.
          </p>

          <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-3">
            <button data-testid="harborstone-find-wisdom-btn" onClick={() => nav("/")}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full text-white text-base font-semibold transition-colors"
              style={{ backgroundColor: ACCENT }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = ACCENT_DARK)}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = ACCENT)}>
              Find Wisdom <ArrowRight className="w-5 h-5" />
            </button>
            <button data-testid="harborstone-lifeguide-btn" onClick={lifeguide}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full text-base font-semibold border-2 bg-white transition-colors"
              style={{ borderColor: ACCENT, color: ACCENT_DARK }}>
              Explore LifeGuide
            </button>
          </div>
        </div>
      </section>

      {/* About Harborstone */}
      <section className="max-w-5xl mx-auto px-5 sm:px-8 pb-14">
        <div data-testid="harborstone-about" className="rounded-3xl border border-[#E2E8F0] bg-white px-6 sm:px-10 py-9">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold mb-5" style={{ color: ACCENT }}>About Harborstone</p>
          <div className="grid sm:grid-cols-3 gap-6 sm:gap-8">
            <Detail icon={Clock} title="Sunday services" body="9:00 & 10:45 AM" accent={ACCENT} />
            <Detail icon={MapPin} title="Where to find us" body="1250 Harborstone Way" accent={ACCENT} />
            <Detail icon={Users} title="Who we are" body="A welcoming community helping people grow in faith and care for one another." accent={ACCENT} />
          </div>
        </div>
      </section>

      {/* Care team */}
      <section className="max-w-5xl mx-auto px-5 sm:px-8 pb-20">
        <div data-testid="harborstone-care" className="rounded-3xl px-6 sm:px-10 py-9 text-center sm:text-left sm:flex sm:items-center sm:justify-between gap-6"
          style={{ backgroundColor: `${ACCENT}0F`, border: `1px solid ${ACCENT}33` }}>
          <div className="flex items-start gap-4 justify-center sm:justify-start">
            <span className="grid place-items-center w-11 h-11 rounded-2xl shrink-0" style={{ backgroundColor: ACCENT }}>
              <HeartHandshake className="w-6 h-6 text-white" strokeWidth={1.7} />
            </span>
            <div>
              <h3 className="font-serif-display text-2xl text-[#0F172A]">Need to talk with someone?</h3>
              <p className="text-[#475569] mt-1 leading-relaxed">We would love to walk with you. Reach out and we will respond with care.</p>
            </div>
          </div>
          <button data-testid="harborstone-care-btn" onClick={() => nav("/contact")}
            className="mt-5 sm:mt-0 w-full sm:w-auto shrink-0 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-white font-semibold transition-colors"
            style={{ backgroundColor: ACCENT_DARK }}>
            Connect with the Harborstone Care Team
          </button>
        </div>
        <p className="text-center text-xs text-[#94A3B8] mt-8">
          This is a demonstration of a church-partner landing page within I Need a Proverb. Harborstone Community Church, its address, and service times are fictional.
        </p>
      </section>
    </div>
  );
}

function Detail({ icon: Icon, title, body, accent }) {
  return (
    <div className="flex items-start gap-3">
      <span className="grid place-items-center w-9 h-9 rounded-xl shrink-0" style={{ backgroundColor: `${accent}1A`, color: accent }}>
        <Icon className="w-5 h-5" strokeWidth={1.8} />
      </span>
      <div>
        <div className="text-[11px] uppercase tracking-widest text-[#94A3B8] font-semibold mb-1">{title}</div>
        <p className="text-[15px] text-[#334155] leading-relaxed">{body}</p>
      </div>
    </div>
  );
}
